import { DatePipe } from '@angular/common';
import { CplUserRequest } from '../../model/cpl-user-request.model';
import { CplRequestItem } from '../../model/cpl-request-item.model';
import { CplA48UserSubmissionDetail } from '../../model/a48/cpl-a48-user-submission-detail.model';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { Component, Input, ChangeDetectionStrategy, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { A48UserStatus } from '../../model/enum/a48-user-status.enum';
import { A48SubmissionContextCode } from 'src/app/model/enum/a48-submission-context-code.enum';
import { TimeTrackingTypes } from 'src/app/model/enum/cpl-time-tracking-type.enum';
import { AccessibilityHelpersService } from '../../services/accessibility/accessibility-helpers.service';
import { A48ActivityType } from 'src/app/model/enum/a48-activity-type.enum';
import { A48HelpersService } from 'src/app/services/a48-helpers/a48-helpers.service';
import { SessionService } from '@edl-development/edl-common';
import { AccessRoleHelper } from '../../model/helpers/access-role.helper';
@Component({
    selector: 'timetracking-status-indicator',
    styleUrls: ['./timetracking-status-indicator.component.scss'],
    templateUrl: './timetracking-status-indicator.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeTrackingStatusIndicatorComponent implements OnInit, OnDestroy {
    @Input() userRequest: CplUserRequest;
    @Input() requestItem: CplRequestItem;
    @Input() get statusId(): number {
        return this._statusId;
    }
    set statusId(value: number) {
        this._statusId = value;
        
        if (!this.userRequest) {
            this.statusLabel = this.getStatusDescriptionSafe(value);
        }
    }
    @Input() paraStatusId: number;
    @Input() isPara: Boolean;
    @Input() paraApplicable: boolean;
    @Input() statusLabel: string;
    @Input() a48ContextCode: A48SubmissionContextCode;
    @Input() timeTrackingType: TimeTrackingTypes;
    @Input() hours: number = 0;
    @Input() activityType: A48ActivityType = A48ActivityType.HOURS;
    @Input() componentIndex: number = 0; // For generating unique IDs when used in lists
    @Input() isUser: boolean = false; // true = My Items view, false = Supervisor view

    // ViewChildren for tooltip references
    @ViewChild('emergencyTooltip', { static: false }) emergencyTooltip: any;
    @ViewChild('paraTooltip', { static: false }) paraTooltip: any;
    @ViewChild('mainTooltip', { static: false }) mainTooltip: any;

    // configs
    @Input() showHours: boolean = false;

    // Custom event emitter enforces role-based permissions before modal access.
    // Using (click) would bypass canOpenModal checks, allowing unauthorized interactions.
    @Output() onIconClick = new EventEmitter<void>();

    // Instance tracking for accessibility
    public emergencyPopoverId: string;
    public paraPopoverId: string;
    public mainPopoverId: string;
    private _statusId: number;



    A48UserStatus = A48UserStatus;
    TimeTrackingTypeEnum = TimeTrackingTypes;

    datePipe = new DatePipe('en-US');

    // Status mappings for accessibility
    private readonly statusMappings = {
        [A48UserStatus.NOACTION]: 'Not Submitted',
        [A48UserStatus.OVERRIDE]: 'Overridden',
        [A48UserStatus.PENDING]: 'Pending approval',
        [A48UserStatus.ERROR]: 'Error - requires attention',
        [A48UserStatus.REMOVED]: 'Removed',
        [A48UserStatus.SUCCESS]: 'Approved',
        [A48UserStatus.PARAAPPROVED]: 'Approved',
        [A48UserStatus.EXCLUDED]: 'Excluded from requirements'
    };

    private _lastFocusOrigin: FocusOrigin = null;

    constructor(
        private accessibilityHelpers: AccessibilityHelpersService,
        private a48HelpersService: A48HelpersService,
        private sessionService: SessionService,
        private focusMonitor: FocusMonitor,
        private elementRef: ElementRef
    ) { }

    get HoursLabel() {
        return this.a48HelpersService.getHoursOrCreditsLabel(this.hours, this.activityType);
    }

    ngOnInit() {
        this.focusMonitor.monitor(this.elementRef, true).subscribe(origin => {
            this._lastFocusOrigin = origin;
        });

        // Generate unique IDs for each popover instance
        this.emergencyPopoverId = this.accessibilityHelpers.generatePopoverId('tt-emergency', this.componentIndex);
        this.paraPopoverId = this.accessibilityHelpers.generatePopoverId('tt-para', this.componentIndex);
        this.mainPopoverId = this.accessibilityHelpers.generatePopoverId('tt-main', this.componentIndex);
        
        if (this.userRequest) {
            this.statusId = this.userRequest.Act48UserSubmissionDetails.StatusID;
            this.paraStatusId = this.userRequest.Act48UserSubmissionDetails.ParaStatusID;
            this.isPara = this.userRequest.Act48UserSubmissionDetails.IsPara;
            this.paraApplicable = this.requestItem?.Act48Detail.ParaApplicable ?? false;
            this.statusLabel = this.getStatusLabel(this.userRequest.Act48UserSubmissionDetails);
            this.a48ContextCode = this.userRequest.Act48UserSubmissionDetails.ContextCode;
            this.hours = this.userRequest.Act48UserSubmissionDetails.Hours;
            this.activityType = this.userRequest.Act48UserSubmissionDetails.ActivityType;
        } else {
            this.statusLabel= this.getStatusDescriptionSafe(this.statusId)
        }
    }

    isEmergencyCert() {
        return this.a48ContextCode === A48SubmissionContextCode.EMERGENCY_CERT_SUBMISSION;
    }

    isParaApplicable() {
        return this.isPara && this.paraApplicable;
    }

    getStatusLabel(userSubmission: CplA48UserSubmissionDetail) {
        if (!userSubmission) {
            return "Act 48 Submission user details not present"
        } else {
            return `${this.getStatusDescriptionSafe(userSubmission.StatusID)} -- Provider: ${userSubmission.Provider} Event: ${userSubmission.EventName} from ${this.datePipe.transform(userSubmission.EventStart)} to ${this.datePipe.transform(userSubmission.EventEnd)}`;
        }
    }

    // #region Click Handling and Permissions
    get canOpenModal(): boolean {
        if (!this.hasAct48Details()) return false;

        return this.isUser || AccessRoleHelper.isLimitedSupervisor(this.sessionService.user.AccessRoleID);
    }

    private hasAct48Details(): boolean {
        return !!(this.userRequest?.Act48UserSubmissionDetails || this.statusId != null);
    }

    handleClick(event: Event) {
        if (this.canOpenModal) {
            event.stopPropagation(); // Prevent event from bubbling if handled
            this.onIconClick.emit();
        }
    }
    // #endregion

    // #region WCAG Accessibility Compliance Helper Methods

    getAccessibleStatusText(): string {
        const primaryStatus = this.getStatusDescriptionSafe(this.statusId);
        const secondaryStatuses: string[] = [];

        if (this.isEmergencyCert()) {
            secondaryStatuses.push('Emergency Certification');
        }

        if (this.isParaApplicable()) {
            const paraStatus = this.getStatusDescriptionSafe(this.paraStatusId);
            secondaryStatuses.push(`Paraeducator status: ${paraStatus}`);
        }

        const context = this.timeTrackingType === TimeTrackingTypes.FLEX ? 'Flex hours tracking' : 'Act 48';

        return this.accessibilityHelpers.createAccessibleStatusText(
            primaryStatus,
            secondaryStatuses,
            context
        );
    }

    getStatusDescription(statusId: number | null | undefined): string {
        return this.getStatusDescriptionSafe(statusId);
    }

    getAriaRole(): string {
        return this.accessibilityHelpers.getAriaRole('status');
    }

    getAriaLive(): string {
        const isUrgent = this.statusId === A48UserStatus.ERROR;
        return this.accessibilityHelpers.getAriaLive(isUrgent);
    }

    getAriaAttributes(popoverId: string): { [key: string]: string | boolean } {
        return this.accessibilityHelpers.createAriaAttributes({
            label: this.getAccessibleStatusText(),
            describedBy: this.accessibilityHelpers.isPopoverActive(popoverId) ? popoverId : undefined,
            role: this.getAriaRole(),
            live: this.getAriaLive() as 'polite' | 'assertive',
            hasPopup: true,
            expanded: this.accessibilityHelpers.isPopoverActive(popoverId)
        });
    }
    
    // Enhanced tooltip event handlers for exclusive popover management
    onTooltipMouseEnter(tooltip: any, popoverId: string): void {
        this.accessibilityHelpers.showPopoverExclusive(tooltip, popoverId);
    }
    
    onTooltipMouseLeave(popoverId: string): void {
        this.accessibilityHelpers.closePopoverByInstance(popoverId);
    }
    
    onTooltipFocus(tooltip: any, popoverId: string): void {
        if (this._lastFocusOrigin === 'keyboard') {
            this.accessibilityHelpers.showPopoverExclusive(tooltip, popoverId);
        }
    }
    
    onTooltipBlur(popoverId: string): void {
        // Small delay to allow moving to tooltip content
        setTimeout(() => {
            if (!this.accessibilityHelpers.isPopoverActive(popoverId)) {
                this.accessibilityHelpers.closePopoverByInstance(popoverId);
            }
        }, 150);
    }
    
    onTooltipKeyDown(event: KeyboardEvent, tooltip: any, popoverId: string): void {
        if (event.key === 'Escape') {
            this.accessibilityHelpers.closePopoverByInstance(popoverId);
            // Return focus to trigger element
            (event.target as HTMLElement)?.focus();
        } else if (event.key === 'Enter' || event.key === ' ') {
            // Dual-purpose handler: clickable icons open modals, non-clickable show tooltips
            if (this.canOpenModal) {
                event.preventDefault();
                this.handleClick(event);
            } else {
                event.preventDefault();
                if (this.accessibilityHelpers.isPopoverActive(popoverId)) {
                    this.accessibilityHelpers.closePopoverByInstance(popoverId);
                } else {
                    this.accessibilityHelpers.showPopoverExclusive(tooltip, popoverId);
                }
            }
        }
    }

    isPopoverActive(popoverId: string): boolean {
        return this.accessibilityHelpers.isPopoverActive(popoverId);
    }

    private getStatusDescriptionSafe(statusId: number | null | undefined): string {
        if (statusId === null || statusId === undefined) {
            return this.statusLabel || 'Status not available';
        }
        return this.accessibilityHelpers.getStatusDescription(statusId, this.statusMappings);
    }
    // #endregion

    ngOnDestroy(): void {
        this.focusMonitor.stopMonitoring(this.elementRef);

        // Clean up any open popovers for this component instance
        this.accessibilityHelpers.closePopoverByInstance(this.emergencyPopoverId);
        this.accessibilityHelpers.closePopoverByInstance(this.paraPopoverId);
        this.accessibilityHelpers.closePopoverByInstance(this.mainPopoverId);
    }
}